import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'innermate.sidebar.collapsed';
const SYNC_EVENT = 'innermate:sidebar';

function readInitial(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(STORAGE_KEY) === '1';
}

function write(next: boolean) {
  window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
  window.dispatchEvent(new CustomEvent<boolean>(SYNC_EVENT, { detail: next }));
}

export function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState<boolean>(readInitial);

  useEffect(() => {
    const onSync = (e: Event) => {
      const next = (e as CustomEvent<boolean>).detail;
      setCollapsed(typeof next === 'boolean' ? next : readInitial());
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setCollapsed(e.newValue === '1');
    };
    window.addEventListener(SYNC_EVENT, onSync);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SYNC_EVENT, onSync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // ⌘\ / Ctrl\ 로 사이드바 토글. 입력 중에도 동작 (기존 ⌘K 와 동일 정책).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.code === 'Backslash') {
        e.preventDefault();
        const next = !readInitial();
        write(next);
        setCollapsed(next);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const toggle = useCallback(() => {
    const next = !readInitial();
    write(next);
    setCollapsed(next);
  }, []);

  const open = useCallback(() => {
    write(false);
    setCollapsed(false);
  }, []);

  /**
   * 강제 collapse. dashboard.create tool 이 시작되는 시점에 채팅 영역에 자리를 내주려고
   * 1회 호출. toggle 과 달리 현재 상태와 무관하게 항상 접힘.
   */
  const close = useCallback(() => {
    write(true);
    setCollapsed(true);
  }, []);

  return { collapsed, toggle, open, close };
}
